import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ImpersonationService } from 'src/app/core/auth/impersonation.service';
import { ImpersonationState } from 'src/app/core/auth/impersonation.interface';
import { AppRoutes } from 'src/app/shared/routes/routes';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-impersonation-banner',
  templateUrl: './impersonation-banner.component.html',
  styleUrls: ['./impersonation-banner.component.scss'],
})
export class ImpersonationBannerComponent implements OnInit, OnDestroy {
  impersonationState: ImpersonationState | null = null;
  isVisible = false;
  isStopping = false;

  private readonly destroy$ = new Subject<void>();

  private readonly impersonationService = inject(ImpersonationService);
  private readonly router = inject(Router);

  ngOnInit(): void {
    // Subscribe to impersonation state changes
    this.impersonationService.impersonationState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.impersonationState = state;
        this.isVisible = state.isActive;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Stop impersonation and return to admin session
   */
  stopImpersonation(): void {
    if (this.isStopping) return;

    const userName = this.impersonationState?.impersonatedUser?.name || 'this user';

    Swal.fire({
      title: 'Exit Impersonation?',
      html: `
        <div style="text-align: left; padding: 10px 0;">
          <p>You are about to stop impersonating:</p>
          <p style="margin: 10px 0; padding: 10px; background: #f8f9fa; border-radius: 6px;">
            <strong style="font-size: 16px; color: #2c3e50;">${userName}</strong>
          </p>
          <p style="color: #6c757d; font-size: 14px;">
            You will be returned to your administrator account.
          </p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: '<i class="fas fa-sign-out-alt"></i> Yes, Exit',
      cancelButtonText: '<i class="fas fa-times"></i> Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      reverseButtons: true,
      focusCancel: true,
      customClass: {
        popup: 'impersonation-exit-modal',
        confirmButton: 'btn btn-danger',
        cancelButton: 'btn btn-secondary'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.isStopping = true;

        // Show loading state
        Swal.fire({
          title: 'Exiting Impersonation...',
          html: 'Please wait while we restore your admin session.',
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        this.impersonationService
          .stopImpersonation('User manually ended the impersonation session')
          .subscribe({
            next: () => {
              this.isStopping = false;

              Swal.fire({
                icon: 'success',
                title: 'Session Ended',
                html: `
                  <div style="text-align: center; padding: 10px 0;">
                    <p>Impersonation session has been successfully terminated.</p>
                    <p style="color: #6c757d; font-size: 14px; margin-top: 10px;">
                      Returning to your administrator account...
                    </p>
                  </div>
                `,
                timer: 2000,
                timerProgressBar: true,
                showConfirmButton: false,
                allowOutsideClick: false
              }).then(() => {
                this.router.navigate([AppRoutes.dashboard.dashboardUnique]);
              });
            },
            error: (error) => {
              console.error('Error stopping impersonation:', error);
              this.isStopping = false;

              Swal.fire({
                icon: 'error',
                title: 'Error',
                html: `
                  <div style="text-align: left; padding: 10px 0;">
                    <p><strong>Failed to end impersonation session.</strong></p>
                    <p style="color: #6c757d; font-size: 14px; margin-top: 10px;">
                      ${error.message || 'An unexpected error occurred. Please try again.'}
                    </p>
                  </div>
                `,
                confirmButtonText: 'OK',
                confirmButtonColor: '#dc3545',
                customClass: {
                  confirmButton: 'btn btn-danger'
                }
              });
            },
          });
      }
    });
  }

  /**
   * Get time remaining until token expires
   */
  getTimeRemaining(): string {
    if (!this.impersonationState?.expiresAt) return '';

    const now = new Date();
    const expiresAt = new Date(this.impersonationState.expiresAt);
    const diffMs = expiresAt.getTime() - now.getTime();

    if (diffMs <= 0) {
      // Token expired - trigger auto-stop if not already stopping
      if (!this.isStopping) {
        console.warn('Token expired detected in banner - triggering auto-stop');
        this.handleExpiredToken();
      }
      return 'Expired';
    }

    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const seconds = Math.floor((diffMs % 60000) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m remaining`;
    } else {
      return `${minutes}m ${seconds}s remaining`;
    }
  }

  /**
   * Check if token is about to expire (less than 10 minutes)
   */
  isAboutToExpire(): boolean {
    if (!this.impersonationState?.expiresAt) return false;

    const now = new Date();
    const expiresAt = new Date(this.impersonationState.expiresAt);
    const diffMs = expiresAt.getTime() - now.getTime();
    const diffMinutes = diffMs / 60000;

    return diffMinutes <= 10 && diffMinutes > 0;
  }

  /**
   * Handle expired token detected by banner
   */
  private handleExpiredToken(): void {
    this.isStopping = true;

    Swal.fire({
      icon: 'warning',
      title: 'Session Expired',
      html: `
        <div style="text-align: left; padding: 10px 0;">
          <p><strong>Your impersonation session has expired.</strong></p>
          <p style="color: #6c757d; font-size: 14px; margin-top: 10px;">
            You will be automatically returned to your administrator account.
          </p>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: {
        popup: 'impersonation-expired-popup'
      },
      didOpen: () => {
        this.impersonationService
          .stopImpersonation('Session expired automatically')
          .subscribe();
      }
    }).then(() => {
      Swal.fire({
        icon: 'info',
        title: 'Returning to Admin',
        html: 'Restoring your administrator session...',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        timer: 1500,
        didOpen: () => {
          Swal.showLoading();
        }
      }).then(() => {
        this.router.navigate([AppRoutes.dashboard.dashboardUnique]);
      });
    });
  }

  /**
   * Get tooltip content with full details
   */
  getTooltipContent(): string {
    if (!this.impersonationState?.impersonatedUser) return '';

    const user = this.impersonationState.impersonatedUser;
    const time = this.getTimeRemaining();
    const role = user.roles?.join(', ') || 'N/A';
    const warning = this.isAboutToExpire()
      ? '\n Session expiring soon!'
      : '';

    return `Impersonating User
            ━━━━━━━━━━━━━━
            Name: ${user.name}
            Email: ${user.email}
            Role: ${role}
            Time remaining: ${time}${warning}

            Click × to exit impersonation`;
  }
}
